// Daily check-in card. Top of the Aura tab. One claim per UTC day.
// v3: no credits — grants a free Aura/Fit score token every day, bonus
// tokens + a Featured submission pass on day 7, and a custom photo
// Featured pass on day 14 (3× on day 30). Day 3 is no longer a perk
// milestone (re-pricing rollout — try-on submit now costs 3 credits).
import { useState } from "react";
import { Flame, Gift, Loader2, Crown, Camera, Sparkles, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useDailyStreak } from "@/hooks/useDailyStreak";
import { useUserPerks } from "@/hooks/useUserPerks";
import { toast } from "sonner";
import { isNativeApp } from "@/lib/platform";
import { HowToDownloadDialog } from "@/components/HowToDownloadDialog";

interface Props {
  className?: string;
}

const DOTS = 7;

const MILESTONES: { day: number; label: string; icon: typeof Crown }[] = [
  { day: 7,  label: "+2 tokens & Featured pass", icon: Sparkles },
  { day: 14, label: "Custom photo feature",      icon: Camera },
  { day: 30, label: "3× custom feature",         icon: Crown },
];

const BONUS_COPY: Record<string, string> = {
  featured_pass: "Featured submission pass unlocked",
  tokens_bonus: "+2 bonus free score tokens",
  tokens_and_featured_pass: "+2 score tokens & 1 Featured submission pass 🎉",
  custom_pass: "Custom photo Featured pass unlocked",
  custom_pass_x3: "3× Custom Featured passes unlocked 👑",
};

const WebStreakLockedCard = ({ className }: Props) => {
  const [howOpen, setHowOpen] = useState(false);
  return (
    <>
      <section
        className={cn(
          "relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/15 via-background to-accent/10 p-4 shadow-soft",
          className,
        )}
        aria-label="Daily streak (app only)"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
            <Flame className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight">Daily streak — app only</p>
            <p className="text-[11px] text-muted-foreground">
              Download FitAuraAI - Try On Clothes on Google Play to start your streak.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setHowOpen(true)}
            className="shrink-0 transition-transform active:scale-95"
            aria-label="See how to get FitAuraAI"
          >
            <span className="inline-flex h-9 items-center gap-1 rounded-full bg-foreground px-3 text-[11px] font-bold text-background">
              <Smartphone className="h-3.5 w-3.5" /> Get app
            </span>
          </button>
        </div>
      </section>
      <HowToDownloadDialog open={howOpen} onOpenChange={setHowOpen} />
    </>
  );
};

export const DailyStreakCard = ({ className }: Props) => {
  // Web (desktop + mobile web) — streak is app-only.
  if (!isNativeApp()) return <WebStreakLockedCard className={className} />;


  const { state, loading, claiming, claim } = useDailyStreak();
  const { wallet, refresh: refreshPerks } = useUserPerks();

  if (loading) return null;

  const onClick = async () => {
    const r = await claim();
    if (!r.ok) {
      toast.error("Couldn't check in. Try again.");
      return;
    }
    await refreshPerks();
    if (r.alreadyClaimed) {
      toast.message("Already checked in today", {
        description: "Come back tomorrow to keep your streak alive.",
      });
      return;
    }
    const bonusLine = r.bonusKind ? BONUS_COPY[r.bonusKind] : null;
    toast.success(`Day ${r.streak} · +1 free score`, {
      description: bonusLine ?? "Use your free Aura or Fit score before it expires in 48h.",
    });
  };

  const filled = Math.min(state.currentStreak, DOTS);
  const nextMilestone = MILESTONES.find((m) => m.day > state.currentStreak);

  return (
    <section
      className={cn(
        "relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/15 via-background to-accent/10 p-4 shadow-soft",
        className,
      )}
      aria-label="Daily streak"
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-primary-foreground shadow-md",
            state.currentStreak > 0
              ? "bg-gradient-to-br from-orange-500 to-red-500"
              : "bg-muted text-muted-foreground",
          )}
        >
          <Flame className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight">
            {state.currentStreak > 0 ? `${state.currentStreak}-day streak` : "Start your streak"}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {state.canClaim
              ? "Check in for a free Aura or Fit score"
              : "Checked in ✓ · come back tomorrow"}
          </p>
        </div>
        <Button
          size="sm"
          onClick={onClick}
          disabled={claiming || !state.canClaim}
          className="h-9 rounded-full gradient-primary text-primary-foreground disabled:opacity-60"
        >
          {claiming ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : state.canClaim ? (
            <><Gift className="mr-1 h-4 w-4" /> Claim</>
          ) : (
            "Done"
          )}
        </Button>
      </div>

      {/* 7-day dot row — day 7 is the milestone, shown as a wider sparkle pip */}
      <div className="mt-3 flex items-center gap-1.5">
        {Array.from({ length: DOTS }).map((_, i) => {
          const isMilestone = i === DOTS - 1;
          const isFilled = i < filled;
          if (isMilestone) {
            return (
              <span
                key={i}
                className={cn(
                  "flex h-3 w-5 shrink-0 items-center justify-center rounded-full ring-1 transition-colors",
                  isFilled
                    ? "bg-gradient-to-r from-orange-500 to-red-500 ring-orange-400/40"
                    : "bg-muted ring-border",
                )}
                aria-label="Day 7 milestone"
              >
                <Sparkles className={cn("h-2 w-2", isFilled ? "text-white" : "text-muted-foreground")} />
              </span>
            );
          }
          return (
            <span
              key={i}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors",
                isFilled ? "bg-gradient-to-r from-orange-500 to-red-500" : "bg-muted",
              )}
            />
          );
        })}
      </div>

      {/* Wallet chips */}
      {(wallet.scoreTokens > 0 || wallet.featuredPasses > 0 || wallet.customFeaturedPasses > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {wallet.scoreTokens > 0 && wallet.tokensActive && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
              <Gift className="h-3 w-3" /> {wallet.scoreTokens} free score{wallet.scoreTokens > 1 ? "s" : ""}
            </span>
          )}
          {wallet.featuredPasses > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent/30 px-2 py-0.5 text-[10px] font-semibold">
              <Sparkles className="h-3 w-3" /> {wallet.featuredPasses} featured pass{wallet.featuredPasses > 1 ? "es" : ""}
            </span>
          )}
          {wallet.customFeaturedPasses > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-semibold text-orange-600 dark:text-orange-300">
              <Camera className="h-3 w-3" /> {wallet.customFeaturedPasses} custom-photo pass{wallet.customFeaturedPasses > 1 ? "es" : ""}
            </span>
          )}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Best: {state.bestStreak} days</span>
        {nextMilestone ? (
          <span className="inline-flex items-center gap-1">
            <nextMilestone.icon className="h-3 w-3" />
            Day {nextMilestone.day}: {nextMilestone.label}
          </span>
        ) : (
          <span>7d featured · 14d custom · 30d crown</span>
        )}
      </div>
    </section>
  );
};
